import React, { useState } from 'react';
import { Select, Tooltip, Typography } from 'antd';

import { StepUpCancelled } from '../../../../stepup/useStepUp.js';
import { candidateOption, isPayoutLine, isStale, itemsOf, offersClaim, payoutOption } from './statementLines.js';

const { Text } = Typography;

/**
 * The Transaction ID cell (design 8). A matched line shows the order's
 * transaction ref (a debit the payout it confirmed). An open credit with no
 * claim on it offers its candidates instead - the fund's claimable
 * subscriptions, read when the picker opens (GET cash/lines/{id}/candidates):
 * each its transaction ref, holder, what it is still owed (and how much of
 * its amount is in or claimed, once any is) and S1/S3 tier, and "Part
 * payment" when the line is less than it is owed - IP-176: a short line pays
 * part of the subscription, which then waits for the rest (statementLines.js
 * candidateOption, its tooltip saying so). Choosing one
 * claims it (MATCH_CLAIM, step-up: the code bound to {lineId, movementId});
 * another person then approves the claim. A refusal is said under the picker;
 * one that means the line moved on reads the list again.
 *
 * IP-184: an unmatched debit offers its fund's unpaid redemption payouts the
 * same way (statementLines.js payoutOption) - its transaction ref, holder,
 * what it still owes, its dealing date and tier - and a matched debit names
 * the redemption it paid.
 *
 * @param {{ line: object, canMatch: boolean, api: object, withStepUp: Function, onClaimed: () => void, onStale: () => void }} props
 */
const StatementMatchCell = ({ line, canMatch, api, withStepUp, onClaimed, onStale }) => {
    const [options, setOptions] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [claiming, setClaiming] = useState(false);

    if (!offersClaim(line)) {
        // IP-202: the matched transaction's strategy activation code under its reference.
        return line.transactionRef
            ? (
                <span className="txn-id">
                    {line.transactionRef}
                    {line.strategyActivationCode && <><br /><Text type="secondary">Strategy {line.strategyActivationCode}</Text></>}
                </span>
            )
            : <Text type="secondary">—</Text>;
    }

    const payout = isPayoutLine(line);

    const load = async (open) => {
        if (!open || options || loading) return;
        setLoading(true);
        setError(null);
        try {
            setOptions(itemsOf(await api.cash.candidates(line.lineId), 'candidates').map(payout ? payoutOption : candidateOption));
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    };

    const claim = async (movementId) => {
        if (!movementId) return;
        const params = { lineId: line.lineId, movementId };
        setClaiming(true);
        setError(null);
        try {
            await withStepUp('MATCH_CLAIM', params, (stepUp) => api.cash.claim(line.lineId, { movementId, stepUp }));
            onClaimed();
        } catch (e) {
            if (e instanceof StepUpCancelled) return;
            setError(e.message);
            if (isStale(e)) onStale();
        } finally {
            setClaiming(false);
        }
    };

    const picker = (
        <Select
            showSearch
            size="small"
            className="statement-candidates"
            style={{ width: 280 }}
            // IP-176: an option says what is owed and how much is in - wider than the cell, so it is not cut off.
            popupMatchSelectWidth={420}
            placeholder={canMatch ? (payout ? 'Claim a redemption…' : 'Claim a transaction…') : 'Unmatched'}
            disabled={!canMatch || claiming}
            loading={loading || claiming}
            value={null}
            onOpenChange={load}
            onChange={claim}
            optionFilterProp="label"
            options={options ?? []}
            notFoundContent={loading ? 'Loading…' : <Text type="secondary">{error ?? (payout ? 'No unpaid redemptions in this fund' : 'No claimable subscriptions in this fund')}</Text>}
            optionRender={({ data }) => (data.title ? <Tooltip title={data.title}><span>{data.label}</span></Tooltip> : data.label)}
        />
    );
    return (
        <div>
            {canMatch ? picker : <Tooltip title="Your roles do not allow claiming a match.">{picker}</Tooltip>}
            {error && <div><Text type="danger" className="statement-cell-error">{error}</Text></div>}
        </div>
    );
};

export default StatementMatchCell;
